import React, { useState, useMemo, Component, type ErrorInfo, type ReactNode } from 'react'
import { useParams, useNavigate } from 'react-router-dom'
import { Clock, TrendingUp, CheckCircle2, Circle, AlertTriangle, User, Users, BarChart3, RefreshCw } from 'lucide-react'
import { useSpace, useIssues, useStatuses, useIssueDetail, useIssueMutations } from '../hooks'
import { useUserOptions } from '../../../shared/hooks/useUserOptions'
import { useAuth } from '../../../context/AuthContext'
import { cn } from '../utils'
import { canonicalDepartmentName, isSuperAdminOrOwner } from '@jira-clone/shared-types'
import { IssueModal } from '../components/IssueModal'
import { useQueryClient } from '@tanstack/react-query'
import { cardClassName } from '@jira-clone/ui'

// Safe Error Boundary to prevent any blank page crashes
class DashboardErrorBoundary extends Component<{ children: ReactNode }, { hasError: boolean; error: Error | null }> {
  constructor(props: { children: ReactNode }) {
    super(props)
    this.state = { hasError: false, error: null }
  }

  static getDerivedStateFromError(error: Error) {
    return { hasError: true, error }
  }

  componentDidCatch(error: Error, errorInfo: ErrorInfo) {
    console.error('EmployeeDashboardPage crashed:', error, errorInfo)
  }

  render() {
    if (this.state.hasError) {
      return (
        <div className="flex h-full w-full min-h-[400px] flex-col items-center justify-center p-6 text-center">
          <AlertTriangle className="h-12 w-12 text-amber-500 mb-3" />
          <h2 className="text-base font-bold text-gray-900 dark:text-slate-100">Unable to display dashboard</h2>
          <p className="text-xs text-gray-500 dark:text-slate-400 mt-1 max-w-md">
            {this.state.error?.message || 'An unexpected error occurred while rendering the dashboard.'}
          </p>
          <button
            onClick={() => this.setState({ hasError: false, error: null })}
            className="mt-4 inline-flex items-center gap-1.5 rounded-lg bg-blue-600 px-3.5 py-1.5 text-xs font-semibold text-white hover:bg-blue-700"
          >
            <RefreshCw className="h-3.5 w-3.5" />
            Retry
          </button>
        </div>
      )
    }
    return this.props.children
  }
}

function formatHours(hours: number): string {
  if (!hours || isNaN(hours) || hours <= 0) return '0h'
  if (hours < 1) return `${Math.round(hours * 60)}m`
  if (hours >= 24) {
    const days = Math.floor(hours / 24)
    const rem = hours % 24
    return `${days}d ${rem > 0 ? `${Math.round(rem)}h` : ''}`
  }
  return `${hours.toFixed(1)}h`
}

function isTaskDone(issue: any): boolean {
  if (!issue) return false
  if (issue.status_category === 'done' || issue.status === 'done') return true
  if (typeof issue.status === 'object' && (issue.status?.category === 'done' || issue.status?.isCompleted)) return true
  return false
}

function getStatusInfo(issue: any, statuses: any[]) {
  if (issue?.status && typeof issue.status === 'object') {
    return {
      name: String(issue.status.name || issue.status.label || 'Todo'),
      category: String(issue.status.category || issue.status_category || 'todo'),
    }
  }
  const statusVal = issue?.statusId || issue?.status
  const found = statuses?.find?.((s: any) => s.id === statusVal || s.name === statusVal)
  if (found) {
    return {
      name: String(found.name || 'Todo'),
      category: String(found.category || issue?.status_category || 'todo'),
    }
  }
  return {
    name: typeof statusVal === 'string' && statusVal ? statusVal : 'Todo',
    category: typeof issue?.status_category === 'string' ? issue.status_category : 'todo',
  }
}

function getAssigneeName(issue: any, userOptions: any[]): string {
  const assignees = Array.isArray(issue?.assigneeIds) ? issue.assigneeIds : []
  const rawAssignee = assignees[0] || issue?.assignee_id || issue?.assignee
  if (!rawAssignee) return 'Unassigned'
  if (typeof rawAssignee === 'object' && rawAssignee.name) return String(rawAssignee.name)
  const rawId = typeof rawAssignee === 'object' ? String(rawAssignee._id || rawAssignee.id || '') : String(rawAssignee)
  const found = userOptions?.find?.((u: any) => u.id === rawId || u.username === rawId)
  return found?.display_name || found?.name || 'Unassigned'
}

function isSameDepartment(deptA?: string | null, deptB?: string | null): boolean {
  if (!deptA || !deptB) return false
  const a = canonicalDepartmentName(deptA).trim().toLowerCase()
  const b = canonicalDepartmentName(deptB).trim().toLowerCase()
  if (a === b) return true
  const isDevOrTech = (d: string) => d.includes('dev') || d.includes('tech')
  if (isDevOrTech(a) && isDevOrTech(b)) return true
  const isDesign = (d: string) => d.includes('creat') || d.includes('design')
  if (isDesign(a) && isDesign(b)) return true
  return false
}

function EmployeeDashboardContent() {
  const params = useParams<{ id?: string; spaceId?: string }>()
  const spaceId = params.id || params.spaceId || ''
  const navigate = useNavigate()
  const { user, isLoading: authLoading } = useAuth()
  const { data: space } = useSpace(spaceId ? spaceId : null)
  const { data: rawIssues = [], isLoading: issuesLoading } = useIssues(spaceId ? spaceId : null)
  const { data: statuses = [] } = useStatuses(spaceId ? spaceId : null)
  const { data: userOptions = [] } = useUserOptions(spaceId ? spaceId : null)

  const [filter, setFilter] = useState<'all' | 'active' | 'completed'>('all')
  const [viewMode, setViewMode] = useState<'overview' | 'my-tasks'>('overview')
  const [selectedIssueId, setSelectedIssueId] = useState<string | null>(null)
  const [isIssueModalOpen, setIsIssueModalOpen] = useState(false)
  const queryClient = useQueryClient()
  const { update, remove } = useIssueMutations(spaceId ? spaceId : null)

  const { data: selectedIssueDetail } = useIssueDetail(selectedIssueId)

  const handleOpenIssue = (issue: any) => {
    const id = issue?.id || issue?._id || issue?.key || null
    if (!id) return
    setSelectedIssueId(String(id))
    setIsIssueModalOpen(true)
  }

  const handleCloseIssueModal = () => {
    setIsIssueModalOpen(false)
    setSelectedIssueId(null)
  }

  const allIssues = useMemo(() => {
    return Array.isArray(rawIssues) ? rawIssues : []
  }, [rawIssues])

  const isSuperAdmin = Boolean(
    (user?.role && isSuperAdminOrOwner(user.role)) ||
    user?.department?.toLowerCase() === 'superadmin'
  )

  // Early return for missing spaceId
  if (!spaceId) {
    return (
      <div className="flex h-full min-h-[300px] items-center justify-center">
        <p className="text-sm text-gray-500">No space selected</p>
      </div>
    )
  }

  // Dynamically resolve user's department
  const userDepartment = useMemo(() => {
    if (user?.department && user.department.trim()) return user.department.trim()
    const fromOptions = userOptions.find((u: any) => u.id === user?.id || u.username === user?.username)?.department
    if (fromOptions && fromOptions.trim()) return fromOptions.trim()
    const fromSpace = space?.members?.find((m: any) => m.userId === user?.id)?.department
    if (fromSpace && fromSpace.trim()) return fromSpace.trim()
    return ''
  }, [user?.department, user?.id, user?.username, userOptions, space?.members])

  // Collect member IDs belonging to the same department
  const departmentMemberIds = useMemo(() => {
    const ids = new Set<string>()
    if (user?.id) ids.add(String(user.id))
    if (user?.username) ids.add(String(user.username))
    if (!userDepartment) return ids
    for (const u of userOptions) {
      if (u?.id && isSameDepartment(u.department, userDepartment)) {
        ids.add(String(u.id))
        if (u.username) ids.add(String(u.username))
      }
    }
    if (space?.members) {
      for (const m of space.members) {
        if (m?.userId && isSameDepartment(m.department, userDepartment)) {
          ids.add(String(m.userId))
        }
      }
    }
    return ids
  }, [userDepartment, userOptions, space?.members, user?.id, user?.username])

  // Filter issues belonging ONLY to user's team / department
  const departmentIssues = useMemo(() => {
    if (!allIssues.length) return []
    if (!userDepartment && departmentMemberIds.size === 0) return allIssues
    return allIssues.filter((issue: any) => {
      if (issue.team && isSameDepartment(issue.team, userDepartment)) return true
      const assignees = Array.isArray(issue.assigneeIds)
        ? issue.assigneeIds.map((a: any) => (typeof a === 'object' ? String(a._id || a.id) : String(a)))
        : []
      const singleAssignee = issue.assignee_id ? String(issue.assignee_id) : (issue.assignee ? String(typeof issue.assignee === 'object' ? issue.assignee._id || issue.assignee.id : issue.assignee) : '')
      const hasDeptAssignee = (
        (singleAssignee && departmentMemberIds.has(singleAssignee)) ||
        assignees.some((aId: string) => departmentMemberIds.has(aId))
      )
      if (hasDeptAssignee) return true
      if (issue.team && !isSameDepartment(issue.team, userDepartment)) return false
      return false
    })
  }, [allIssues, userDepartment, departmentMemberIds])

  // All team members' tasks (excluding subtasks) strictly scoped to user's department
  const allTasks = useMemo(() => {
    if (!departmentIssues.length) return []
    return departmentIssues.filter((issue: any) => !issue.parent_id && !issue.parentId)
  }, [departmentIssues])

  // Current user's tasks
  const myTasks = useMemo(() => {
    if (!user?.id || !allTasks.length) return []
    const currentUserId = String(user.id)
    const currentUsername = user.username ? String(user.username) : ''
    return allTasks.filter((issue: any) => {
      const assignees = Array.isArray(issue.assigneeIds)
        ? issue.assigneeIds.map((a: any) => (typeof a === 'object' ? String(a._id || a.id) : String(a)))
        : []
      const singleAssignee = issue.assignee_id ? String(issue.assignee_id) : (issue.assignee ? String(typeof issue.assignee === 'object' ? issue.assignee._id || issue.assignee.id : issue.assignee) : '')
      return (
        assignees.includes(currentUserId) ||
        (currentUsername && assignees.includes(currentUsername)) ||
        singleAssignee === currentUserId ||
        (currentUsername && singleAssignee === currentUsername)
      )
    })
  }, [allTasks, user?.id, user?.username])

  // Superadmin: show all project tasks without department filtering
  const adminAllTasks = useMemo(() => {
    if (!isSuperAdmin) return allTasks
    return allIssues.filter((issue: any) => !issue.parent_id && !issue.parentId)
  }, [isSuperAdmin, allIssues, allTasks])

  const adminMyTasks = useMemo(() => {
    if (!isSuperAdmin || !user?.id || !adminAllTasks.length) return []
    const currentUserId = String(user.id)
    const currentUsername = user.username ? String(user.username) : ''
    return adminAllTasks.filter((issue: any) => {
      const assignees = Array.isArray(issue.assigneeIds)
        ? issue.assigneeIds.map((a: any) => (typeof a === 'object' ? String(a._id || a.id) : String(a)))
        : []
      const singleAssignee = issue.assignee_id ? String(issue.assignee_id) : (issue.assignee ? String(typeof issue.assignee === 'object' ? issue.assignee._id || issue.assignee.id : issue.assignee) : '')
      return (
        assignees.includes(currentUserId) ||
        (currentUsername && assignees.includes(currentUsername)) ||
        singleAssignee === currentUserId ||
        (currentUsername && singleAssignee === currentUsername)
      )
    })
  }, [isSuperAdmin, adminAllTasks, user?.id, user?.username])

  // Member stats: superadmin sees all members, regular users see department only
  const memberStats = useMemo(() => {
    const userMap = new Map<string, { name: string; email: string }>()
    for (const u of userOptions) {
      if (u?.id) {
        userMap.set(u.id, { name: u.display_name || u.name || 'Unknown', email: u.email || '' })
      }
    }

    const sourceIssues = isSuperAdmin ? allIssues : departmentIssues
    const allowedIds = isSuperAdmin
      ? new Set(userOptions.map((u: any) => u.id).filter(Boolean))
      : departmentMemberIds

    const map = new Map<string, { name: string; email: string; tasks: any[]; completed: number; active: number }>()
    for (const issue of sourceIssues) {
      const assignees = Array.isArray((issue as any).assigneeIds) ? (issue as any).assigneeIds : []
      const rawAssignee = assignees[0] || issue.assignee_id || (issue as any).assignee
      if (!rawAssignee) continue
      const key = typeof rawAssignee === 'object' ? String(rawAssignee._id || rawAssignee.id || 'unknown') : String(rawAssignee)
      const info = userMap.get(key) || {
        name: typeof rawAssignee === 'object' && rawAssignee.name ? rawAssignee.name : 'Team Member',
        email: typeof rawAssignee === 'object' && rawAssignee.email ? rawAssignee.email : '',
      }
      if (!map.has(key)) {
        map.set(key, { ...info, tasks: [], completed: 0, active: 0 })
      }
      const entry = map.get(key)!
      entry.tasks.push(issue)
      if (isTaskDone(issue)) {
        entry.completed++
      } else {
        entry.active++
      }
    }
    return Array.from(map.entries())
      .filter(([id]) => allowedIds.has(id))
      .map(([id, data]) => ({ id, ...data }))
  }, [isSuperAdmin, allIssues, departmentIssues, userOptions, departmentMemberIds])

  const effectiveTasks = isSuperAdmin ? adminAllTasks : allTasks
  const effectiveMyTasks = isSuperAdmin ? adminMyTasks : myTasks

  // Filtered tasks based on view mode and filter
  const displayedTasks = useMemo(() => {
    const source = viewMode === 'my-tasks' ? effectiveMyTasks : effectiveTasks
    if (filter === 'active') {
      return source.filter((t: any) => !isTaskDone(t))
    }
    if (filter === 'completed') {
      return source.filter((t: any) => isTaskDone(t))
    }
    return source
  }, [viewMode, filter, effectiveTasks, effectiveMyTasks])

  // Project progress stats
  const stats = useMemo(() => {
    const total = effectiveTasks.length
    const completed = effectiveTasks.filter((t: any) => isTaskDone(t)).length
    const active = total - completed
    const inProgress = effectiveTasks.filter((t: any) => {
      if (t?.status_category === 'in_progress' || t?.status === 'in_progress') return true
      if (typeof t?.status === 'object' && t?.status?.category === 'in_progress') return true
      return false
    }).length
    const totalTimeSpent = effectiveTasks.reduce((sum: number, t: any) => sum + (Number(t.timeSpent || t.time_spent) || 0), 0)
    const totalTimeRemaining = effectiveTasks.reduce(
      (sum: number, t: any) => sum + (Number(t.timeRemaining ?? t.time_remaining) || 0),
      0
    )
    const estimatedTotal = effectiveTasks.reduce((sum: number, t: any) => sum + (Number(t.estimatedHours || t.estimated_hours) || 0), 0)
    const avgProgress = total > 0
      ? Math.round(effectiveTasks.reduce((sum: number, t: any) => {
          const est = Number(t.estimatedHours || t.estimated_hours) || 0
          const spent = Number(t.timeSpent || t.time_spent) || 0
          return sum + (est > 0 ? Math.min((spent / est) * 100, 100) : (isTaskDone(t) ? 100 : 0))
        }, 0) / total)
      : 0

    return {
      total,
      completed,
      active,
      inProgress,
      totalTimeSpent,
      totalTimeRemaining,
      estimatedTotal,
      avgProgress,
      completionRate: total > 0 ? Math.round((completed / total) * 100) : 0,
    }
  }, [effectiveTasks])

  return (
    <div className="flex h-full w-full flex-col bg-white dark:bg-slate-950 font-sans overflow-hidden">
      {/* Sub-header Banner */}
      <div className="border-b border-gray-200/90 bg-white/95 dark:border-slate-800/90 dark:bg-slate-900/95 backdrop-blur-md flex-shrink-0 shadow-2xs">
        <div className="flex flex-wrap items-center justify-between gap-4 px-4 md:px-6 pt-3 pb-2.5">
          <div className="flex items-center gap-3 min-w-0">
            <div className="flex h-10 w-10 items-center justify-center rounded-xl bg-gradient-to-br from-orange-500 to-rose-600 text-white shadow-lg shadow-orange-500/20">
              <User className="h-5 w-5" />
            </div>
            <div>
              <div className="text-[11px] font-semibold text-gray-400 dark:text-slate-500 uppercase tracking-wider">
                {isSuperAdmin ? 'Superadmin View' : 'Employee View'}
              </div>
              <h1 className="text-lg font-bold text-gray-900 dark:text-slate-100 tracking-tight">Project Progress</h1>
              <p className="text-xs text-gray-500 dark:text-slate-400">
                {user?.name || user?.email || 'Employee'} — {space?.name || 'Project'}
              </p>
            </div>
          </div>

          {/* View Mode Toggle */}
          <div className="flex items-center gap-1.5 bg-gray-100 dark:bg-slate-800 rounded-lg p-1">
            <button
              onClick={() => setViewMode('overview')}
              className={cn(
                'px-3 py-1.5 rounded-md text-xs font-semibold transition-all cursor-pointer',
                viewMode === 'overview'
                  ? 'bg-white dark:bg-slate-700 text-gray-900 dark:text-slate-100 shadow-xs'
                  : 'text-gray-600 dark:text-slate-400 hover:text-gray-900 dark:hover:text-slate-200'
              )}
            >
              <BarChart3 className="h-3.5 w-3.5 inline mr-1" />
              Overview
            </button>
            <button
              onClick={() => setViewMode('my-tasks')}
              className={cn(
                'px-3 py-1.5 rounded-md text-xs font-semibold transition-all cursor-pointer',
                viewMode === 'my-tasks'
                  ? 'bg-white dark:bg-slate-700 text-gray-900 dark:text-slate-100 shadow-xs'
                  : 'text-gray-600 dark:text-slate-400 hover:text-gray-900 dark:hover:text-slate-200'
              )}
            >
              <User className="h-3.5 w-3.5 inline mr-1" />
              My Tasks ({effectiveMyTasks.length})
            </button>
          </div>
        </div>

        {/* Filter bar */}
        <div className="flex items-center gap-2 px-4 md:px-6 py-2 border-t border-gray-100 dark:border-slate-800/80 bg-gray-50/50 dark:bg-slate-900/50">
          <span className="text-xs font-medium text-gray-500 dark:text-slate-400">Filter:</span>
          {(['all', 'active', 'completed'] as const).map((f) => (
            <button
              key={f}
              onClick={() => setFilter(f)}
              className={cn(
                'px-2.5 py-1 rounded-md text-xs font-medium capitalize transition-colors cursor-pointer',
                filter === f
                  ? 'bg-blue-600 text-white shadow-2xs'
                  : 'text-gray-600 dark:text-slate-400 hover:bg-gray-200/60 dark:hover:bg-slate-800'
              )}
            >
              {f}
            </button>
          ))}
          {issuesLoading && (
            <span className="ml-auto flex items-center gap-1.5 text-xs text-gray-400">
              <RefreshCw className="h-3 w-3 animate-spin" />
              Syncing tasks…
            </span>
          )}
        </div>
      </div>

      {/* Content Area */}
      <div className="flex-1 overflow-y-auto p-4 md:p-6 scrollbar-thin">
        {viewMode === 'overview' ? (
          <>
            {/* Overview KPI Cards */}
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 mb-6">
              <div className="rounded-xl border border-gray-200 bg-gradient-to-br from-blue-50/80 to-blue-100/40 dark:border-slate-800 dark:bg-slate-900/80 p-4 shadow-2xs">
                <div className="flex items-center justify-between mb-2">
                  <span className="text-xs font-semibold text-blue-700 dark:text-blue-300 uppercase tracking-wide">
                    Total Tasks
                  </span>
                  <TrendingUp className="h-4 w-4 text-blue-600 dark:text-blue-400" />
                </div>
                <div className="text-2xl font-bold text-blue-900 dark:text-blue-100">{stats.total}</div>
                <div className="text-xs text-blue-600/80 dark:text-blue-400/80 mt-1">
                  {stats.active} active, {stats.completed} completed
                </div>
              </div>

              <div className="rounded-xl border border-gray-200 bg-gradient-to-br from-emerald-50/80 to-emerald-100/40 dark:border-slate-800 dark:bg-slate-900/80 p-4 shadow-2xs">
                <div className="flex items-center justify-between mb-2">
                  <span className="text-xs font-semibold text-emerald-700 dark:text-emerald-300 uppercase tracking-wide">
                    Completion Rate
                  </span>
                  <CheckCircle2 className="h-4 w-4 text-emerald-600 dark:text-emerald-400" />
                </div>
                <div className="text-2xl font-bold text-emerald-900 dark:text-emerald-100">{stats.completionRate}%</div>
                <div className="text-xs text-emerald-600/80 dark:text-emerald-400/80 mt-1">
                  {stats.completed} of {stats.total} tasks
                </div>
              </div>

              <div className="rounded-xl border border-gray-200 bg-gradient-to-br from-purple-50/80 to-purple-100/40 dark:border-slate-800 dark:bg-slate-900/80 p-4 shadow-2xs">
                <div className="flex items-center justify-between mb-2">
                  <span className="text-xs font-semibold text-purple-700 dark:text-purple-300 uppercase tracking-wide">
                    In Progress
                  </span>
                  <Clock className="h-4 w-4 text-purple-600 dark:text-purple-400" />
                </div>
                <div className="text-2xl font-bold text-purple-900 dark:text-purple-100">{stats.inProgress}</div>
                <div className="text-xs text-purple-600/80 dark:text-purple-400/80 mt-1">
                  {formatHours(stats.totalTimeSpent)} spent
                </div>
              </div>

              <div className="rounded-xl border border-gray-200 bg-gradient-to-br from-orange-50/80 to-orange-100/40 dark:border-slate-800 dark:bg-slate-900/80 p-4 shadow-2xs">
                <div className="flex items-center justify-between mb-2">
                  <span className="text-xs font-semibold text-orange-700 dark:text-orange-300 uppercase tracking-wide">
                    My Workload
                  </span>
                  <User className="h-4 w-4 text-orange-600 dark:text-orange-400" />
                </div>
                <div className="text-2xl font-bold text-orange-900 dark:text-orange-100">{effectiveMyTasks.length}</div>
                <div className="text-xs text-orange-600/80 dark:text-orange-400/80 mt-1">
                  {effectiveMyTasks.filter((t) => !isTaskDone(t)).length} tasks pending for you
                </div>
              </div>
            </div>

            {/* Team Breakdown Section */}
            {memberStats.length > 0 && (
              <div className={cn(cardClassName(), 'mb-6 p-4')}>
                <div className="flex items-center justify-between mb-3">
                  <h2 className="text-xs font-bold text-gray-900 dark:text-slate-100 uppercase tracking-wide flex items-center gap-1.5">
                    <Users className="h-4 w-4 text-indigo-500" />
                    Team Member Distribution
                  </h2>
                </div>
                <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3">
                  {memberStats.map((member: any) => (
                    <div
                      key={member.id}
                      className="rounded-lg border border-gray-100 bg-gray-50/60 dark:border-slate-800 dark:bg-slate-800/40 p-3"
                    >
                      <div className="flex items-center justify-between">
                        <span className="text-xs font-semibold text-gray-800 dark:text-slate-200 truncate">
                          {member.name}
                        </span>
                        <span className="text-[11px] font-bold text-blue-600 dark:text-blue-400">
                          {member.tasks.length} tasks
                        </span>
                      </div>
                      <div className="flex items-center gap-3 mt-1.5 text-[11px] text-gray-500 dark:text-slate-400">
                        <span>{member.active} active</span>
                        <span>•</span>
                        <span className="text-emerald-600 dark:text-emerald-400">{member.completed} done</span>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* All Tasks List */}
            <div>
              <h2 className="text-xs font-bold text-gray-900 dark:text-slate-100 uppercase tracking-wide mb-3">
                All Space Tasks ({displayedTasks.length})
              </h2>
              <div className="space-y-2.5">
                {displayedTasks.length === 0 ? (
                  <div className="flex flex-col items-center justify-center py-12 text-center rounded-xl border border-dashed border-gray-200 dark:border-slate-800">
                    <CheckCircle2 className="h-10 w-10 text-gray-300 dark:text-slate-600 mb-2" />
                    <p className="text-xs font-semibold text-gray-600 dark:text-slate-400">No tasks found</p>
                  </div>
                ) : (
                  displayedTasks.map((issue: any) => {
                    const statusInfo = getStatusInfo(issue, statuses)
                    const done = isTaskDone(issue)
                    const assigneeName = getAssigneeName(issue, userOptions)
                    const timeSpent = Number(issue.timeSpent || issue.time_spent) || 0
                    const timeRemaining = issue.timeRemaining ?? issue.time_remaining ?? null
                    const estimated = Number(issue.estimatedHours || issue.estimated_hours) || 0
                    const progress = estimated > 0 ? Math.min((timeSpent / estimated) * 100, 100) : (done ? 100 : 0)

                    return (
                      <div
                        key={issue.id || issue._id || Math.random()}
                        onClick={() => handleOpenIssue(issue)}
                        className={cn(cardClassName({ interactive: true }), 'p-3.5 hover:border-blue-400/60 transition-all')}
                      >
                        <div className="flex items-start justify-between gap-3">
                          <div className="flex-1 min-w-0">
                            <div className="flex items-center gap-2 mb-1.5">
                              {done ? (
                                <CheckCircle2 className="h-4 w-4 text-emerald-600 dark:text-emerald-400 shrink-0" />
                              ) : (
                                <Circle className="h-4 w-4 text-gray-400 dark:text-slate-500 shrink-0" />
                              )}
                              <h3 className="text-xs font-semibold text-gray-900 dark:text-slate-100 truncate">
                                {issue.key ? `${issue.key}: ` : (issue.number ? `TASK-${issue.number}: ` : '')}
                                {issue.title || 'Untitled task'}
                              </h3>
                            </div>

                            <div className="flex items-center gap-3 flex-wrap text-[11px]">
                              <span
                                className={cn(
                                  'inline-flex items-center rounded-full px-2 py-0.5 text-[10px] font-bold uppercase tracking-wide',
                                  done
                                    ? 'bg-emerald-100 text-emerald-700 dark:bg-emerald-900/30 dark:text-emerald-300'
                                    : statusInfo.category === 'in_progress'
                                    ? 'bg-blue-100 text-blue-700 dark:bg-blue-900/30 dark:text-blue-300'
                                    : 'bg-gray-100 text-gray-700 dark:bg-slate-800 dark:text-slate-300'
                                )}
                              >
                                {statusInfo.name}
                              </span>

                              <span className="text-gray-500 dark:text-slate-400 flex items-center gap-1">
                                <User className="h-3 w-3" />
                                {assigneeName}
                              </span>

                              <span className="text-gray-500 dark:text-slate-400 flex items-center gap-1">
                                <Clock className="h-3 w-3" />
                                {formatHours(timeSpent)} spent
                                {timeRemaining !== null && ` • ${formatHours(Number(timeRemaining))} left`}
                              </span>

                              {estimated > 0 && (
                                <span className="text-gray-400 dark:text-slate-500">
                                  Est: {formatHours(estimated)}
                                </span>
                              )}
                            </div>

                            {/* Progress bar */}
                            {estimated > 0 && (
                              <div className="mt-2.5">
                                <div className="w-full h-1.5 bg-gray-100 dark:bg-slate-800 rounded-full overflow-hidden">
                                  <div
                                    className={cn(
                                      'h-full rounded-full transition-all duration-300',
                                      done
                                        ? 'bg-emerald-500'
                                        : progress > 80
                                        ? 'bg-amber-500'
                                        : 'bg-blue-600'
                                    )}
                                    style={{ width: `${progress}%` }}
                                  />
                                </div>
                              </div>
                            )}
                          </div>
                        </div>
                      </div>
                    )
                  })
                )}
              </div>
            </div>
          </>
        ) : (
          /* My Tasks View */
          <div>
            <div className="mb-4">
              <h2 className="text-xs font-bold text-gray-900 dark:text-slate-100 uppercase tracking-wide">
                Tasks Assigned to You ({displayedTasks.length})
              </h2>
              <p className="text-xs text-gray-500 dark:text-slate-400 mt-0.5">
                Personal task list for {user?.name || user?.email || 'you'} in this space
              </p>
            </div>

            <div className="space-y-2.5">
              {displayedTasks.length === 0 ? (
                <div className="flex flex-col items-center justify-center py-16 text-center rounded-xl border border-dashed border-gray-200 dark:border-slate-800">
                  <CheckCircle2 className="h-10 w-10 text-gray-300 dark:text-slate-600 mb-2" />
                  <p className="text-xs font-semibold text-gray-600 dark:text-slate-400">
                    {filter === 'all' ? 'You have no assigned tasks in this space' : `No ${filter} tasks found`}
                  </p>
                </div>
              ) : (
                displayedTasks.map((issue: any) => {
                  const statusInfo = getStatusInfo(issue, statuses)
                  const done = isTaskDone(issue)
                  const timeSpent = Number(issue.timeSpent || issue.time_spent) || 0
                  const timeRemaining = issue.timeRemaining ?? issue.time_remaining ?? null
                  const estimated = Number(issue.estimatedHours || issue.estimated_hours) || 0

                  return (
                    <div
                      key={issue.id || issue._id || Math.random()}
                      onClick={() => handleOpenIssue(issue)}
                      className={cn(cardClassName({ interactive: true }), 'p-3.5 hover:border-blue-400/60 transition-all')}
                    >
                      <div className="flex items-start justify-between gap-3">
                        <div className="flex-1 min-w-0">
                          <div className="flex items-center gap-2 mb-1.5">
                            {done ? (
                              <CheckCircle2 className="h-4 w-4 text-emerald-600 dark:text-emerald-400 shrink-0" />
                            ) : (
                              <Circle className="h-4 w-4 text-gray-400 dark:text-slate-500 shrink-0" />
                            )}
                            <h3 className="text-xs font-semibold text-gray-900 dark:text-slate-100 truncate">
                              {issue.key ? `${issue.key}: ` : (issue.number ? `TASK-${issue.number}: ` : '')}
                              {issue.title || 'Untitled task'}
                            </h3>
                          </div>

                          <div className="flex items-center gap-3 flex-wrap text-[11px]">
                            <span
                              className={cn(
                                'inline-flex items-center rounded-full px-2 py-0.5 text-[10px] font-bold uppercase tracking-wide',
                                done
                                  ? 'bg-emerald-100 text-emerald-700 dark:bg-emerald-900/30 dark:text-emerald-300'
                                  : statusInfo.category === 'in_progress'
                                  ? 'bg-blue-100 text-blue-700 dark:bg-blue-900/30 dark:text-blue-300'
                                  : 'bg-gray-100 text-gray-700 dark:bg-slate-800 dark:text-slate-300'
                              )}
                            >
                              {statusInfo.name}
                            </span>

                            <span className="text-gray-500 dark:text-slate-400 flex items-center gap-1">
                              <Clock className="h-3 w-3" />
                              {formatHours(timeSpent)} spent
                              {timeRemaining !== null && ` • ${formatHours(Number(timeRemaining))} left`}
                            </span>

                            {estimated > 0 && (
                              <span className="text-gray-400 dark:text-slate-500">
                                Est: {formatHours(estimated)}
                              </span>
                            )}
                          </div>
                        </div>

                        <div className="shrink-0">
                          <span
                            className={cn(
                              'inline-flex items-center gap-1 rounded-full px-2 py-0.5 text-[10px] font-bold uppercase',
                              done
                                ? 'bg-emerald-50 text-emerald-700 dark:bg-emerald-950/40 dark:text-emerald-300'
                                : 'bg-blue-50 text-blue-700 dark:bg-blue-950/40 dark:text-blue-300'
                            )}
                          >
                            {done ? '✓ Done' : '○ Active'}
                          </span>
                        </div>
                      </div>
                    </div>
                  )
                })
              )}
            </div>
          </div>
        )}
      </div>

      {/* Deep-linked Issue Modal */}
      {isIssueModalOpen && selectedIssueDetail && (
        <IssueModal
          issue={selectedIssueDetail}
          open={isIssueModalOpen}
          onClose={handleCloseIssueModal}
          statuses={statuses}
          members={userOptions}
          epics={(allIssues as any[])?.filter?.((i: any) => i.type === 'epic') ?? []}
          departments={(space as any)?.departments}
          onUpdate={async (id, payload) => {
            await update.mutateAsync({ id, payload })
          }}
          onDelete={async (id) => {
            await remove.mutateAsync(id)
            handleCloseIssueModal()
          }}
        />
      )}
    </div>
  )
}

export function EmployeeDashboardPage() {
  return (
    <DashboardErrorBoundary>
      <EmployeeDashboardContent />
    </DashboardErrorBoundary>
  )
}
